import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import ComponentMenu from "layouts/pages/connector/components/ComponentMenu";
import { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Tiles from "./tiles";
import moment from "moment";
import DataTable from "cxfcomponents/Tables/DataTable";
import { UserContext } from "context/AppProvider/UserContext";
import { useRoles } from "hooks/useRoles";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import defaultConnectorIcon from "assets/images/connector-list-view.svg";
import StatusBadge from "cxfcomponents/StatusBadge";
import { PERMISSIONS } from "constants/portal";
import { useAuth0 } from "@auth0/auth0-react";
import { useSharedConnectors } from "hooks/useSharedConnectors";
import UsersCell from "layouts/Flows/components/UsersCell";
import { useSelector } from "react-redux";
import { TenantContext } from "context/AppProvider/TenantContext";
import { permissionChecker } from "helpers/common";
import { useResourceListener } from "hooks/useResourceListener";
import { useOwnedConnectors } from "hooks/useOwnedConnectors";

const options = {
	status: [
		{ value: "active", label: "Active" },
		{ value: "deleted", label: "Deleted" }
	]
};

export const LibraryConnectors = () => {
	const [libraryView, setLibraryView] = useState("list");
	const { ownedConnectors: connectors, handleFetchOwnedConnectors } =
		useOwnedConnectors();
	const { handleFetchSharedConnectors } = useSharedConnectors();
	const { user } = useAuth0();
	const { userData } = useContext(UserContext);
	const { roles } = useRoles(false);
	const { userPermissions } = useContext(PermissionsContext);
	const { users } = useSelector((store) => store?.portal?.users);
	const { sharedConnectors } = useSelector(
		(store) => store?.portal?.connectors
	);
	const { tenantData } = useContext(TenantContext);
	useResourceListener("connectorsSharingUpdate", [
		handleFetchConnectors,
		handleFetchOwnedConnectors
	]);

	const handleFilterConnector = (connector) => {
		const userRole = roles?.find((role) => role.id === userData?.role_id);

		if (
			userRole?.name?.toLowerCase() === "owner" ||
			permissionChecker(userPermissions, PERMISSIONS.VIEW_ANY_CONNECTORS)
		) {
			return true;
		}

		const foundSharedConnector = sharedConnectors.find(
			({ connector_id, shared_user_id }) =>
				connector_id === connector.id && shared_user_id === user.cxfUserId
		);

		if (foundSharedConnector || connector.created_by_id === user?.cxfUserId)
			return true;

		return false;
	};

	function handleFetchConnectors() {
		handleFetchSharedConnectors();
		handleFetchOwnedConnectors();
	}

	const tableData = useMemo(() => {
		return {
			columns: [
				{
					Header: "developer_logo",
					accessor: "developer_logo",
					show: false,
					canGroupBy: false,
					disableSortBy: true
				},
				{
					Header: "logo",
					accessor: "logo",
					canGroupBy: false,
					disableSortBy: true,
					Cell: ({ value }) => (
						<SoftBox
							width="60px"
							height="60px"
						>
							{value}
						</SoftBox>
					)
				},
				{
					Header: "name",
					accessor: "name",
					width: 250,
					canGroupBy: false,
					Cell: ({ row, value }) => (
						<SoftTypography
							fontSize="14px"
							fontWeight="normal"
						>
							<Link
								style={{ color: "#2D3748" }}
								className="white-to-dark"
								to={`/connector/${row.original.id}`}
							>
								{value}
							</Link>
						</SoftTypography>
					)
				},
				{
					Header: "developer",
					accessor: "developer",
					width: 250,
					canGroupBy: false,
					Cell: ({ row, value }) => (
						<SoftBox
							sx={{
								display: "flex",
								alignItems: "center"
							}}
						>
							<SoftBox
								sx={{
									width: "32px",
									height: "32px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
									overflow: "hidden"
								}}
							>
								{row.values.developer_logo}
							</SoftBox>
							<SoftTypography
								marginLeft="2px"
								fontWeight="bold"
								fontSize="12px"
							>
								{value}
							</SoftTypography>
						</SoftBox>
					)
				},
				{
					Header: "status",
					accessor: "status",
					canGroupBy: false,
					Cell: ({ value }) => {
						return <StatusBadge status={value ?? "active"} />;
					}
				},
				{
					Header: "users",
					accessor: "users",
					canGroupBy: false,
					Cell: ({ value }) => {
						return <UsersCell users={value} />;
					},
					disableSortBy: true
				},
				{
					Header: "last edited",
					accessor: "updated_date_time",
					width: 100,
					canGroupBy: false,
					disableSortBy: false,
					defaultSortOrder: "desc",
					sortType: (rowA, rowB, columnId) => new Date(rowA.values[columnId]) - new Date(rowB.values[columnId]),
					Cell: ({ value }) => (
						<SoftTypography
							fontSize="14px"
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "start"
							}}
						>
							<span>{value && moment(value).format("MM/DD/YYYY")}</span>
							<span>{value && moment(value).format("h:mm:ss A")}</span>
						</SoftTypography>
					)
				},
				{
					Header: "cost",
					accessor: "cost",
					canGroupBy: false,
					Cell: ({ value }) => (
						<SoftTypography
							fontSize="14px"
							fontWeight="bold"
						>
							{value}
						</SoftTypography>
					)
				},
				{
					Header: "actions",
					accessor: "action",
					canGroupBy: false,
					disableSortBy: true
				},
				{
					accessor: "id",
					show: false
				},
				{
					accessor: "description",
					show: false
				}
			].map((column) => ({ ...column, disableResize: true })),
			rows: [
				...connectors
					.filter(handleFilterConnector)
					.sort((firstConnector, secondConnector) => new Date(secondConnector.updated_date_time || secondConnector.created_date_time || 0) - new Date(firstConnector.updated_date_time || firstConnector.created_date_time || 0))
					.map((component) => {
					const connectorOwner = users.find(
						(user) => user.auth0_id === component.created_by_id
					);
					const filteredSharedUsers = sharedConnectors
						.filter(({ connector_id }) => connector_id === component.id)
						.map((sharedUser) => {
							const foundUser = users.find(
								({ auth0_id }) => auth0_id === sharedUser.shared_user_id
							);

							return foundUser ?? sharedUser;
						});

					return {
						id: component.id,
						name: component.name,

						// update this type
						type: component.component_type
							? component.component_type
							: "Connector",
						updated_date_time: component.updated_date_time || component.created_date_time || null,
						status:
							component.status?.toLowerCase() === "active"
								? "active"
								: component.status,
						users: connectorOwner
							? [connectorOwner].concat(filteredSharedUsers)
							: [],
						cost:
							component.price === null
								? "Free"
								: component.price === 0
									? "Free"
									: component.price,
						logo: (
							<SoftBox
								component="img"
								width="100%"
								height="100%"
								borderRadius="12px"
								border={component.logo ? "0.5px solid #E6E6E6" : "none"}
								src={
									component.logo
										? `${component.logo}?v=${component.updated_date_time ?? ""}`
										: defaultConnectorIcon
								}
							/>
						),
						developer: tenantData?.company_name ?? "",
						developer_logo: tenantData?.logo_url ? (
							<SoftBox
								sx={{
									width: "32px",
									height: "32px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									overflow: "hidden",
									borderRadius: "32px"
								}}
							>
								<SoftBox
									component="img"
									src={tenantData?.logo_url}
									sx={{
										width: "100%",
										height: "100%",
										objectFit: "contain",
										borderRadius: "32px"
									}}
								/>
							</SoftBox>
						) : (
							""
						),
						action: <ComponentMenu component={component} />,
						description: component.description
					};
				})
			]
		};
	}, [connectors, user, userPermissions, userData, roles, sharedConnectors]);

	return (
		<DataTable
			table={tableData}
			entriesPerPage={{ defaultValue: 25 }}
			hiddenTopButtons={false}
			tableView={libraryView}
			setTableView={setLibraryView}
			splitted={true}
			tileViewComponent={(rows) => <Tiles rows={rows} />}
			reloadTableData={handleFetchConnectors}
			withSearchbar={true}
			withTileview={true}
			withConnectorsOptions={true}
			withStatusFilter={true}
			options={options}
			tableHeight="clamp(400px, calc(100vh - 83px), calc(100vh - 83px))"
		/>
	);
};
